/**
 * Утилиты для работы с ценами
 *
 * Сервер хранит цены в копейках (например, 10000 = 100 рублей)
 * Для отображения в UI нужно делить на 100
 */

/**
 * Конвертирует цену из копеек в рубли для отображения
 * @param priceInCents - Цена в копейках (с сервера)
 * @returns Цена в рублях для отображения
 */
export const convertPriceToDisplay = (priceInCents: number): number => {
  return priceInCents / 100;
};

/**
 * Конвертирует цену из рублей обратно в копейки для отправки на сервер
 * @param priceInRubles - Цена в рублях
 * @returns Цена в копейках
 */
export const convertPriceToServer = (priceInRubles: number): number => {
  return Math.round(priceInRubles * 100);
};

/**
 * Форматирует цену для отображения в UI
 * Делит цену на 100 и форматирует через Intl.NumberFormat
 * @param priceInCents - Цена в копейках (с сервера)
 * @param options - Опции форматирования
 * @returns Отформатированная строка цены
 */
export const formatPriceDisplay = (
  priceInCents: number,
  options?: {
    locale?: string;
    currency?: string;
    showCurrency?: boolean;
  }
): string => {
  const {
    locale = "ru-RU",
    currency = "RUB",
    showCurrency = true,
  } = options || {};

  const priceInRubles = convertPriceToDisplay(priceInCents);

  if (showCurrency) {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceInRubles);
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(priceInRubles);
};

/**
 * Форматирует цену как простую строку с символом рубля
 * @param priceInCents - Цена в копейках (с сервера)
 * @returns Строка вида "100 ₽"
 */
export const formatPriceSimple = (priceInCents: number): string => {
  const priceInRubles = convertPriceToDisplay(priceInCents);
  return `${priceInRubles} ₽`;
};
